import type { Rule } from 'ant-design-vue/es/form'
import { JSEncrypt } from 'jsencrypt/lib'

import { getPublicKey } from '~/api'
import type { PublicKeyData } from '~/api/interface'

// 验证手机号
export const validatePhone = (_rule: Rule, value: string) => {
  const { $t } = useNuxtApp()
  const reg =
    /^(13[0-9]|14[01456879]|15[0-35-9]|16[2567]|17[0-8]|18[0-9]|19[0-35-9])\d{8}$/
  if (value === '') {
    return Promise.reject(new Error($t('qing-shu-ru-shou-ji-hao')))
  } else {
    const isOk = reg.test(value)
    if (!isOk) {
      return Promise.reject(
        new Error($t('qing-shu-ru-zheng-que-de-shou-ji-hao'))
      )
    }
    return Promise.resolve()
  }
}

// 确认密码
export const validatePassword = (
  _rule: Rule,
  value: string,
  target: string
) => {
  const { $t } = useNuxtApp()
  if (value === '') {
    return Promise.reject(new Error($t('qing-shu-ru-que-ren-mi-ma')))
  } else {
    if (value !== target) {
      return Promise.reject(
        new Error($t('liang-ci-mi-ma-shu-ru-bu-yi-zhi-qing-zhong-xin-shu-ru'))
      )
    }
    return PwdValidator.validateNewPassword(_rule, value)
  }
}

const getStringLength = (str: string): number => {
  let len = 0
  const chineseRegex = /[\u4E00-\u9FA5]/g
  const chinese = str?.match(chineseRegex)
  if (chinese) {
    len += chinese.length * 2
  }
  return str?.length - (chinese?.length ?? 0) + len
}

// 验证输入文案长度
export const validateName = (
  _rule: Rule,
  value: string,
  max = 16,
  text?: string
) => {
  const { $t } = useNuxtApp()

  const currentLength = getStringLength(value)

  if (currentLength > max) {
    return Promise.reject(
      new Error(
        $t('text-chang-du-chao-guo-bu-de-chao-guo-max-ge-zi-fu', [text, max])
      )
    )
  }

  return Promise.resolve()
}

export const passwordReg =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d|.*[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?])(?!.*\s)(?=.{6,20})/

// 密码正则表达式6~20位任意数字、字母、特殊符号
export const legacyPasswordReg = /^[a-zA-Z0-9.~!@#$%^&*()_\-=+{}[\]]{6,20}$/

// 账号名称
// export const accountNameReg = /^[a-zA-Z0-9]{6,20}$/
// 修改为6-20位字母或数字，必须包含字母
export const accountNameReg = /^(?=.*[a-zA-Z])[a-zA-Z0-9]{6,20}$/
// 手机号正则表达式
export const phoneReg =
  /^(13[0-9]|14[01456879]|15[0-35-9]|16[2567]|17[0-8]|18[0-9]|19[0-35-9])\d{8}$/

// 验证码正则
export const smsCodeReg = /^\d{6}$/

// 身份证号校验
export const idCardReg = /(^\d{15}$)|(^\d{18}$)|(^\d{17}(\d|X|x)$)/

// 账号名称
export const accountReg = /^[a-zA-Z0-9]{6,20}$/

// 邮箱验证
// export const emailReg = /^[A-Za-z0-9]+([-._][A-Za-z0-9]+)*@[A-Za-z0-9]+(-[A-Za-z0-9]+)*(\.[A-Za-z]{2,6}|[A-Za-z]{2,4}\.[A-Za-z]{2,3})$/;

// 密码口令6~20位
// 密码需要是数字、大写字母、小写字母、字符的组合，最少三种或三种以上

export class PwdValidator {
  static isSafeRange(val: string) {
    return val.length >= 6 && val.length <= 20
  }

  static hasNumber(val: string) {
    return /[0-9]/.test(val)
  }

  static hasUpper(val: string) {
    return /[A-Z]/.test(val)
  }

  static hasLower(val: string) {
    return /[a-z]/.test(val)
  }

  static hasSpecial(val: string) {
    return /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(val)
  }

  static isValidFormat(val: string) {
    return (
      Number(PwdValidator.hasNumber(val)) +
        Number(PwdValidator.hasUpper(val)) +
        Number(PwdValidator.hasLower(val)) +
        Number(PwdValidator.hasSpecial(val)) >=
      3
    )
  }

  static validateNewPassword = (_rule: Rule, value: string) => {
    const { $t } = useNuxtApp()

    const isSafe =
      this.isSafeRange(value) &&
      Number(PwdValidator.hasNumber(value)) +
        Number(PwdValidator.hasUpper(value)) +
        Number(PwdValidator.hasLower(value)) +
        Number(PwdValidator.hasSpecial(value)) >=
        3

    if (!isSafe) {
      return Promise.reject(
        new Error(
          $t(
            'mi-ma-xu-yao-shi-shu-zi-da-xie-zi-mu-xiao-xie-zi-mu-zi-fu-de-zu-he-zui-shao-san-zhong-huo-san-zhong-yi-shang'
          )
        )
      )
    }
    return Promise.resolve()
  }
}

export const transformPublicKey = async (
  payload: any
): Promise<[string, PublicKeyData]> => {
  const res = await getPublicKey()

  const payloadString = JSON.stringify(payload)
  const crypt = new JSEncrypt()
  crypt.setPublicKey(res.data!.public_key)

  return [crypt.encrypt(payloadString) as string, res.data!]
}
